/**
 * Turnstile Widget Component
 * Created: 2025-11-08
 * Updated: 2025-11-09 - Added failure counter and graceful degradation after 3 attempts
 * Updated: 2025-11-09 - Added token lifecycle management and auto-expiration
 * 
 * Wrapper for Cloudflare Turnstile CAPTCHA with Polish localization
 * Handles token generation, errors, auto-retry, bypass after failures, and token expiration
 */

import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import { TURNSTILE_CONFIG, TURNSTILE_MESSAGES } from '@/config/turnstile';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { useValuationStore } from '@/hooks/store/useValuationStore';

type TokenState = 'idle' | 'generating' | 'ready' | 'expired' | 'consumed' | 'error';

interface TurnstileWidgetProps {
  onVerify: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
  className?: string;
}

export interface TurnstileWidgetRef {
  reset: () => void;
  getTokenState: () => TokenState;
}

export const TurnstileWidget = forwardRef<TurnstileWidgetRef, TurnstileWidgetProps>(({
  onVerify,
  onError,
  onExpire,
  className = '',
}, ref) => {
  const [status, setStatus] = useState<'idle' | 'loading' | 'error' | 'success'>('idle');
  const [tokenState, setTokenState] = useState<TokenState>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [tokenTimestamp, setTokenTimestamp] = useState<number | null>(null);
  const turnstileRef = useRef<any>(null);
  const expirationCheckInterval = useRef<NodeJS.Timeout | null>(null);
  
  const { 
    turnstileFailureCount, 
    incrementTurnstileFailures, 
    allowBypass,
    setAllowBypass 
  } = useValuationStore();

  // Token expiration time: 4.5 minutes (270 seconds)
  const TOKEN_EXPIRATION_MS = 270000;

  const handleSuccess = (token: string) => {
    const now = Date.now();
    console.log('[Turnstile] Verification successful', {
      domain: window.location.hostname,
      timestamp: new Date(now).toISOString()
    });
    setStatus('success');
    setTokenState('ready');
    setTokenTimestamp(now);
    setErrorMessage('');
    onVerify(token);
    
    // Start expiration check
    startExpirationCheck();
  };

  const handleError = (error?: any) => {
    console.error('[Turnstile] Verification error:', error, {
      domain: window.location.hostname,
      errorCode: error?.code || 'unknown'
    });
    setStatus('error');
    setTokenState('error');
    
    // Check for specific error codes
    const errorCode = error?.code || error?.toString() || '';
    
    // Domain mismatch error (110200)
    if (errorCode.includes('110200')) {
      console.error('[Turnstile] Domain mismatch error - check Cloudflare configuration', {
        currentDomain: window.location.hostname,
        url: window.location.href
      });
      setErrorMessage('Konfiguracja bezpieczeństwa wymaga aktualizacji. Odśwież stronę lub spróbuj ponownie.');
    } else {
      setErrorMessage(TURNSTILE_MESSAGES.error);
    }
    
    // Increment failure counter
    incrementTurnstileFailures();
    
    // After 3 failures, enable bypass mode
    if (turnstileFailureCount + 1 >= 3) {
      setErrorMessage('Problem z weryfikacją. Możesz kontynuować bez weryfikacji.');
      setAllowBypass(true);
    }
    
    onError?.();
    stopExpirationCheck();
  };

  const handleExpire = () => {
    console.log('[Turnstile] Token expired by Cloudflare');
    setStatus('idle');
    setTokenState('expired');
    setTokenTimestamp(null);
    onExpire?.();
    stopExpirationCheck();
  };

  const handleRetry = () => {
    console.log('[Turnstile] Retrying verification');
    setStatus('loading');
    setTokenState('generating');
    setErrorMessage('');
    setTokenTimestamp(null);
    stopExpirationCheck();
    
    // Reset the widget
    if (turnstileRef.current) {
      turnstileRef.current.reset();
    }
  };

  // Start checking for token expiration
  const startExpirationCheck = () => {
    stopExpirationCheck();
    
    expirationCheckInterval.current = setInterval(() => {
      if (tokenTimestamp && tokenState === 'ready') {
        const now = Date.now();
        const age = now - tokenTimestamp;
        
        // Check if token is about to expire (within 30 seconds)
        if (age >= TOKEN_EXPIRATION_MS - 30000 && age < TOKEN_EXPIRATION_MS) {
          console.log('[Turnstile] Token expiring soon, will auto-regenerate');
        }
        
        // Auto-expire token after 4.5 minutes
        if (age >= TOKEN_EXPIRATION_MS) {
          console.log('[Turnstile] Token expired after 4.5 minutes, auto-regenerating');
          setTokenState('expired');
          setStatus('idle');
          setErrorMessage('Sesja wygasła. Weryfikacja zostanie odświeżona.');
          
          // Auto-regenerate after a short delay
          setTimeout(() => {
            handleRetry();
          }, 1000);
          
          stopExpirationCheck();
        }
      }
    }, 5000); // Check every 5 seconds
  };

  const stopExpirationCheck = () => {
    if (expirationCheckInterval.current) {
      clearInterval(expirationCheckInterval.current);
      expirationCheckInterval.current = null;
    }
  };

  // Expose reset method and state getter via ref
  useImperativeHandle(ref, () => ({
    reset: () => {
      console.log('[Turnstile] External reset triggered');
      setTokenState('idle');
      setTokenTimestamp(null);
      stopExpirationCheck();
      handleRetry();
    },
    getTokenState: () => tokenState
  }));

  // Set loading state initially
  useEffect(() => {
    setStatus('loading');
    setTokenState('generating');
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopExpirationCheck();
    };
  }, []);

  return (
    <div className={`flex flex-col items-center gap-3 ${className}`}>
      {/* Loading State */}
      {status === 'loading' && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>{TURNSTILE_MESSAGES.loading}</span>
        </div>
      )}

      {/* Success State */}
      {status === 'success' && (
        <div className="flex items-center gap-2 text-sm text-success">
          <CheckCircle2 className="h-4 w-4" />
          <span>{TURNSTILE_MESSAGES.verified}</span>
        </div>
      )}

      {/* Error State */}
      {status === 'error' && (
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            <span>{errorMessage}</span>
          </div>
          {!allowBypass && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRetry}
              className="mt-1"
            >
              {TURNSTILE_MESSAGES.retry}
            </Button>
          )}
          {allowBypass && (
            <p className="text-xs text-muted-foreground mt-1">
              Formularz jest dostępny - zweryfikujemy Twoje zgłoszenie ręcznie.
            </p>
          )}
        </div>
      )}

      {/* Turnstile Widget (hidden when showing other states) */}
      <div className={status !== 'idle' && status !== 'loading' ? 'hidden' : ''}>
        <Turnstile
          ref={turnstileRef}
          siteKey={TURNSTILE_CONFIG.siteKey}
          onSuccess={handleSuccess}
          onError={handleError}
          onExpire={handleExpire}
          options={{
            theme: TURNSTILE_CONFIG.theme,
            size: TURNSTILE_CONFIG.size,
            language: TURNSTILE_CONFIG.language,
            appearance: TURNSTILE_CONFIG.appearance,
          }}
        />
      </div>
    </div>
  );
});

TurnstileWidget.displayName = 'TurnstileWidget';
